/* eslint-disable no-use-before-define */
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { simpleSetPayload, simpleSetSubField, createSimpleSelector, createSimpleSubFieldSelector, ACTIVITY_LIBRARY_DATA_MAX_TIME } from "../constants";
import initialState from "./initialActivityLibraryState";
import { fetchCategoriesPromise, fetchThemesPromise, fetchGradesPromise, fetchSoundsPromise, fetchAreaOfNeedsPromise, fetchGamesPromise, fetchCCSSMetaPromise } from "@app/routes/vocabulary/api";
import { serializedAreaOfNeeds } from "@app/routes/vocabulary/serializers";
import { SLICE_NAMES } from "./sliceNames";
import { saveTreatmentActivityList as savePlayList } from "@amplio-common/treatments/api";
import { selectTreatmentId } from "./commonSelectorsV2";
import { v4 as uuidv4 } from "uuid";
import { selectExternalSelectedFilter, selectIsOpen } from "@app/common/calendar/components/createSession/V3/store/CreateSessionSlice";
import _ from "lodash";

const simpleFieldSelector = fieldName => createSimpleSelector(SLICE_NAMES.ACTIVITY_LIBRARY, fieldName);
const simpleSubFieldSelector = (field, subField) => createSimpleSubFieldSelector(SLICE_NAMES.ACTIVITY_LIBRARY, field, subField);
const simpleSetAndClearError = stateField => (state, action) => { state[stateField] = action.payload; state.generateGameError = false; };

export const gamesToActiviyListGames = (gameList, treatmentId, doneActivities) => {
  const activityListGames = gameList.map((g, index) => ({
    h5p_activity_list_uuid: g.uuid,
    gameId: g.id,
    objectiveId: g.objective,
    order: index,
    treatmentId,
    is_done: doneActivities?.includes(g.uuid),
  }));

  return activityListGames;
};

export const saveTreatmentGamePlayList = createAsyncThunk(
  `${SLICE_NAMES.ACTIVITY_LIBRARY}/saveTreatmentGamePlayList`, async (payload, thunkAPI) => {
    const { pl } = payload;
    const { getState } = thunkAPI;
    const state = getState();
    const treatmentId = selectTreatmentId(state);
    const doneActivities = selectDoneActivities(state);
    const formattedGamePlayList = gamesToActiviyListGames(pl, treatmentId, doneActivities);

    treatmentId && savePlayList(formattedGamePlayList, treatmentId);
  },
);


export const getFormattedGameList = createAsyncThunk(
  `${SLICE_NAMES.ACTIVITY_LIBRARY}/getFormattedGameList`, async (payload, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState();
    // eslint-disable-next-line no-use-before-define
    const gamePlayList = selectGamePlayList(state);
    // eslint-disable-next-line no-use-before-define
    const allGameList = selectGames(state);

    const gameList = gamePlayList.map((playListGame) => {
      const game = allGameList.find(g => g.id === playListGame.gameId);
      const metadata = game?.game_metadata?.find(mt => mt.objective === playListGame.objectiveId);
      const requiredFormatGame = {
        ...game,
        ...metadata,
        uuid: playListGame?.h5p_activity_list_uuid || uuidv4(),
        is_done: playListGame?.is_done,
      };
      if (game) {
        return requiredFormatGame;
      }

      return undefined;
    });
    return gameList;
  },
);

const buildGameStructure = (games, metaData) => {
  metaData.forEach(md => {
    const gameId = md.game_id;
    if (!games[gameId]) { return; } // no matching game - skip.
    games[gameId].game_metadata = [...(games[gameId].game_metadata || []), md];
  });
  return games;
};

export const fetchLibraryData = createAsyncThunk(
  `${SLICE_NAMES.ACTIVITY_LIBRARY}/fetchLibraryData`,
  async (__, thunkAPI) => {
    // only refetch the data if not currently fetching or after ACTIVITY_LIBRARY_DATA_MAX_TIME expired
    const state = thunkAPI.getState();
    const dataLoadTime = selectDataLoadTime(state);
    const isDataValid = dataLoadTime && new Date().getTime() - dataLoadTime < ACTIVITY_LIBRARY_DATA_MAX_TIME;
    if (isDataValid) { return { valid: true }; }
    //
    const [categories, themes, grades, sounds, areasOfNeed, ccssMeta, games] = await Promise.all([
      fetchCategoriesPromise(), fetchThemesPromise(), fetchGradesPromise(), fetchSoundsPromise(), fetchAreaOfNeedsPromise(),
      fetchCCSSMetaPromise(), fetchGamesPromise(),
    ]);
    return {
      categories,
      themes: themes.response,
      grades: grades.response,
      sounds,
      areasOfNeed: areasOfNeed.response,
      gamesRes: games.response,
      ccssMeta: ccssMeta.response,
    };
  },
);


export const activityLibrarySlice = createSlice({
  name: SLICE_NAMES.ACTIVITY_LIBRARY,
  initialState,
  reducers: {
    clearAllFilter: (state) => ({
      ...state,
      selectedArticulationSoundPositions: [],
      selectedArticulationSounds: [],
      selectedCategories: [],
      selectedGrades: {},
      numOfSyllables: [],
      syllablesShapes: [],
      generateGameError: false,
    }),
    setSelectedCategories: simpleSetPayload("selectedCategories"),
    setSelectedFilter: simpleSetPayload("selectedFilter"),
    setGamePlayList: simpleSetPayload("gamePlayList"),
    setIsShowPreviewGame: simpleSetPayload("isShowPreviewGame"),
    setFormattedGamePlayList: simpleSetPayload("formattedGamePlayList"),
    setIsGamePlayListReady: simpleSetPayload("isGamePlayListReady"),
    setDoneActivities: simpleSetPayload("doneActivities"),
    setCurrentActivity: simpleSetPayload("currentActivity"),
    setTrackEventsFunc: simpleSetPayload("trackEventsFunc"),
    setSelectedArticulationSounds: simpleSetAndClearError("selectedArticulationSounds"),
    setSelectedArticulationSoundPositions: simpleSetAndClearError("selectedArticulationSoundPositions"),
    setSelectedGame: simpleSetAndClearError("selectedGame"),
    setCategories: simpleSetPayload("categories"),
    setSyllablesShapes: simpleSetPayload("syllablesShapes"),
    setNumOfSyllables: simpleSetPayload("numOfSyllables"),
    setSearchTxt: simpleSetPayload("searchText"),
    setSounds: simpleSetPayload("sounds"),
    setGenerateGameError: simpleSetPayload("generateGameError"),
    setIsLoadingCategories: simpleSetPayload("isLoadingCategories"),
    setIsGamePreviewFullscreen: simpleSetPayload("isGamePreviewFullscreen"),
    setCCSSMeta: simpleSetPayload("ccssMeta"),
    setSelectedGrades: simpleSetSubField("selectedFilter", "grades"),
    setSelectedThemes: simpleSetSubField("selectedFilter", "themes"),
    setSelectedCCS: simpleSetSubField("selectedFilter", "ccss"),

    resetGamePlaylist: state => {
      state.selectedFilter = null;
      state.gamePlayList = [];
      state.formattedGamePlayList = [];
      state.isGamePlayListReady = false;
    },
  },
  extraReducers: {
    [fetchLibraryData.pending]: state => {
      state.isLoadingCategories = true;
      state.isLoadingGames = true;
    },
    [fetchLibraryData.fulfilled]: (state, action) => {
      state.isLoadingCategories = false;
      state.isLoadingGames = false;
      if (action.payload?.valid) { return; } // We already have valid data
      const { areasOfNeed, gamesRes: { games, game_meta_data: metaData }, sounds } = action.payload || {};
      const derivedFields = ["areasOfNeed", "gamesRes"];
      Object.entries(action.payload).forEach(([k, v]) => { if (!derivedFields.includes(k)) state[k] = v; });
      state.games = buildGameStructure(games, metaData);
      state.metaData = metaData;
      const obj = serializedAreaOfNeeds(areasOfNeed);
      Object.entries(obj).forEach(([k, v]) => state[k] = v);
      state.dataLoadTime = new Date().getTime();
      state.sounds = sounds;

      // create dictionary of protocol to objective list and field to objective list
      const pDict = {};
      const fDict = {};
      Object.values(state.games).forEach(gm => {
        gm.game_metadata?.forEach(mt => {
          mt.protocol_list?.forEach(pt => {
            const group = pDict[pt] || [];
            if (!group.includes(mt.objective)) {
              group.push(mt.objective);
              pDict[pt] = group;
            }
          });

          mt.field_list.forEach(fl => {
            const group = fDict[fl] || [];
            if (!group.includes(mt.objective)) {
              group.push(mt.objective);
              fDict[fl] = group;
            }
          });
        });
      });
      state.protocolToObjectiveDict = pDict;
      state.fieldToObjectiveDict = fDict;
    },
    [getFormattedGameList.fulfilled]: (state, action) => {
      state.isGamePlayListReady = true;
      state.formattedGamePlayList = action.payload;
      state.doneActivities = state.formattedGamePlayList?.filter(fg => fg.is_done === true).map(doneItem => doneItem.uuid);
    },
    [getFormattedGameList.rejected]: (state) => {
      state.isGamePlayListReady = true;
      state.formattedGamePlayList = [];
    },
  },
});

export default activityLibrarySlice.reducer;

//actions
export const { setSelectedFilter, setSelectedThemes, setSelectedGrades, setGamePlayList, setIsGamePlayListReady, resetGamePlaylist,
  setFormattedGamePlayList, setSelectedCCS, setIsShowPreviewGame,
  setSelectedCategories, setTrackEventsFunc, setSelectedArticulationSounds, setSelectedArticulationSoundPositions,
  setSyllablesShapes, setNumOfSyllables, setSelectedGame, setSearchTxt, setGenerateGameError, setIsLoadingCategories,
  clearAllFilter, setCategories, setSounds, setCCSSMeta, setDoneActivities, setCurrentActivity, setIsGamePreviewFullscreen } = activityLibrarySlice.actions;


//selectors
export const selectCategories = simpleFieldSelector("categories");
export const selectIsLoadingCategories = simpleFieldSelector("isLoadingCategories");
export const selectSelectedCategories = simpleFieldSelector("selectedCategories");
export const selectAreasOfNeed = simpleFieldSelector("areasOfNeed");
export const selectSelectedFilter = simpleFieldSelector("selectedFilter");
export const selectGrades = simpleFieldSelector("grades");
export const selectThemes = simpleFieldSelector("themes");
export const selectCCSSMeta = simpleFieldSelector("ccssMeta");
export const selectDataLoadTime = simpleFieldSelector("dataLoadTime");
export const selectGamePlayList = simpleFieldSelector("gamePlayList");
export const selectIsGamePlayListReady = simpleFieldSelector("isGamePlayListReady");
export const selectIsLoadingGames = simpleFieldSelector("isLoadingGames");
export const selectGameObj = simpleFieldSelector("games");
export const selectMetaData = simpleFieldSelector("metaData");
export const selectFormattedGamePlayList = simpleFieldSelector("formattedGamePlayList");
export const selectIsShowPreviewGame = simpleFieldSelector("isShowPreviewGame");
export const selectCurrentActivity = simpleFieldSelector("currentActivity");
export const selectDoneActivities = simpleFieldSelector("doneActivities");
export const selectSelectedArticulationSounds = simpleFieldSelector("selectedArticulationSounds");
export const selectSounds = simpleFieldSelector("sounds");
export const selectGamesRelations = simpleFieldSelector("gamesRelations");
export const selectTrackEventsFunc = simpleFieldSelector("trackEventsFunc");
export const selectSelectedArticulationSoundPositions = simpleFieldSelector("selectedArticulationSoundPositions");
export const selectNumOfSyllables = simpleFieldSelector("numOfSyllables");
export const selectSyllablesShapes = simpleFieldSelector("syllablesShapes");
export const selectSelectedGame = simpleFieldSelector("selectedGame");
export const selectGenerateGameError = simpleFieldSelector("generateGameError");
export const selectSearchTxt = simpleFieldSelector("searchText");
export const selectProtocolToObjectiveDict = simpleFieldSelector("protocolToObjectiveDict");
export const selectFieldToObjectiveDict = simpleFieldSelector("fieldToObjectiveDict");
export const selectSoundPositions = simpleFieldSelector("soundPositions");

export const selectSelectedGrades = simpleSubFieldSelector("selectedFilter", "grades");
export const selectSelectedThemes = simpleSubFieldSelector("selectedFilter", "themes");
export const selectSelectedCCS = simpleSubFieldSelector("selectedFilter", "ccss");
export const selectIsGamePreviewFullscreen = simpleFieldSelector("isGamePreviewFullscreen");

export const selectGames = createSelector(
  [selectGameObj], gameObj => Object.values(gameObj),
);


export const selectThemesForDropdown = createSelector(
  [selectThemes], themes => themes.map(t => ({ value: `${t.id}`, label: t.name })),
);

export const selectGradesForDropdown = createSelector(
  [selectGrades], grades => grades.map(g => ({ ...g, value: `${g.id}` })),
);

export const selectProtocols = createSelector(
  [selectAreasOfNeed], areasOfNeed => areasOfNeed.reduce((protocols, aon) => {
    aon.area_of_need_set.forEach(s => s.protocols.forEach(p => {
      protocols = [...protocols, { id: p.id, name: p.name }];
    }));
    return protocols;
  }, []),
);

export const selectFilteredGames = createSelector(
  [selectSelectedFilter, selectGames, selectExternalSelectedFilter, selectIsOpen], (selectedFilter, games, externalSelectedFilter, isOpen) => {
    const filter = isOpen ? externalSelectedFilter : selectedFilter;
    let resGrades = true;
    let resThemes = true;
    let resProtocols = true;
    let resAreaOfNeed = true;
    if (!filter) { return games; }
    let result = games.filter(g => {
      if (filter?.ccss) {
        const { grade_id, domain_id, standard_id } = filter?.ccss;
        // const grade_id &&
        return g?.game_metadata?.some(gm => gm?.ccss?.findIndex(gmcc => {
          let resStandard = true;
          let resDomain = true;
          let resGrade = true;
          if (standard_id?.length > 0) {
            resStandard = standard_id.includes(gmcc?.standard_id);
          }
          if (domain_id) {
            resDomain = domain_id && gmcc?.domain_id === domain_id;
          }
          if (grade_id) {
            resGrade = gmcc?.grade_id === grade_id;
          }
          return resStandard && resDomain && resGrade;
        }) > -1);
      }
      if (filter?.grades?.length) {
        resGrades = g?.grades?.some(gr => filter.grades.findIndex(sfg => Number(sfg) === gr.name) > -1);
      }
      if (filter?.themes?.length) {
        resThemes = g?.themes?.some(t => filter.themes?.includes(`${t}`));
      }
      if (filter?.protocol) {
        resProtocols = g?.game_metadata?.some(gm => gm?.protocol_list?.includes(filter.protocol));
      }
      if (filter?.areaOfNeed) {
        resAreaOfNeed = g?.game_metadata?.some(gm => gm?.field_list?.includes(filter.areaOfNeed));
      }
      return resGrades && resThemes && resProtocols && resAreaOfNeed;
    });

    // Filter metadata in filtered games
    if (filter?.ccss) {
      result = _.cloneDeep(result);
      const { grade_id, domain_id, standard_id } = filter?.ccss;

      result.forEach(item => {
        item.game_metadata = item.game_metadata.filter(gm => gm?.ccss?.findIndex(gmcc => {
          let resStandard = true;
          let resDomain = true;
          let resGrade = true;
          if (standard_id?.length > 0) {
            resStandard = standard_id.includes(gmcc?.standard_id);
          }
          if (domain_id) {
            resDomain = domain_id && gmcc?.domain_id === domain_id;
          }
          if (grade_id) {
            resGrade = gmcc?.grade_id === grade_id;
          }
          return resStandard && resDomain && resGrade;
        }) > -1);
        return item;
      });
    }

    return result;
  },
);
